/**
 * Shared trade field formatting for the mobile card / tablet-row layouts.
 *
 * Both the phone TradeCard and the tablet richer-row consume this so formatting
 * stays consistent with the desktop table, and the hardcoded default field lists
 * (Option A — no per-user config) live in ONE place. To change which fields show
 * on the collapsed card later, edit ROW1_FIELDS / ROW2_FIELDS below.
 */

export type TradeFieldKey =
  | 'ticker'
  | 'strategy'
  | 'status'
  | 'strike'
  | 'exp'
  | 'buyDate'
  | 'daysLeft'
  | 'days'
  | 'premium'
  | 'bought'
  | 'current'
  | 'optionPrice'
  | 'qty'
  | 'total'
  | 'itmOtm'
  | 'closeAction'
  | 'closeDate'
  | 'netPnl'
  | 'return'

/** Minimal shape needed for formatting — matches the TradePage `Trade` interface subset. */
export interface TradeLike {
  ticker: string
  action: string
  status: string
  strikePrice: number
  expDate: string
  buyDate: string
  daysLeft: number
  originalDays: number
  daysKept: number | null
  premium: number
  priceWhenBought: number
  currentPrice: number
  currentOptionPrice?: number | null
  qty: number
  total: number
  closeAction: string | null
  closeDate: string | null
  netPosition: number
  returnPct: number
}

/** Human labels for each field key. */
export const FIELD_LABELS: Record<TradeFieldKey, string> = {
  ticker: 'Ticker',
  strategy: 'Action',
  status: 'Status',
  strike: 'Strike',
  exp: 'Exp',
  buyDate: 'Buy Date',
  daysLeft: 'Days Left',
  days: 'Days',
  premium: 'Premium',
  bought: 'Bought',
  current: 'Current',
  optionPrice: 'Opt. Price',
  qty: 'Qty',
  total: 'Total',
  itmOtm: 'ITM/OTM',
  closeAction: 'Close Action',
  closeDate: 'Closed',
  netPnl: 'Net P&L',
  return: 'Return',
}

function money(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  const sign = n < 0 ? '-' : ''
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function mdy(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const parts = String(dateStr).split('-')
  if (parts.length !== 3) return String(dateStr)
  const [y, m, d] = parts
  return `${m}/${d}/${y}`
}

/** ITM/OTM heuristic (matches how the desktop table derives it): compares current price vs strike by action. */
function itmOtm(t: TradeLike): string {
  const price = t.currentPrice
  if (!price || !t.strikePrice) return '—'
  const isPut = t.action === 'CSP' || (t.action || '').toUpperCase().includes('PUT')
  if (isPut) return price < t.strikePrice ? 'ITM' : 'OTM'
  // CC / calls
  return price > t.strikePrice ? 'ITM' : 'OTM'
}

export interface FormattedField {
  key: TradeFieldKey
  label: string
  value: string
  /** semantic tone for coloring (used by netPnl / return / status) */
  tone?: 'pos' | 'neg' | 'muted' | 'none'
}

/** Format a single field for display. */
export function formatTradeField(t: TradeLike, key: TradeFieldKey): FormattedField {
  const label = FIELD_LABELS[key]
  switch (key) {
    case 'ticker':
      return { key, label, value: t.ticker, tone: 'none' }
    case 'strategy':
      return { key, label, value: t.action, tone: 'none' }
    case 'status':
      return { key, label, value: t.closeAction === 'Assigned' ? 'assignment' : t.status, tone: 'none' }
    case 'strike':
      return { key, label, value: money(t.strikePrice), tone: 'none' }
    case 'exp':
      return { key, label, value: mdy(t.expDate), tone: 'none' }
    case 'buyDate':
      return { key, label, value: mdy(t.buyDate), tone: 'none' }
    case 'daysLeft':
      return { key, label, value: t.status === 'open' ? String(t.daysLeft ?? '—') : '—', tone: 'none' }
    case 'days':
      return { key, label, value: String(t.daysKept ?? t.originalDays ?? '—'), tone: 'none' }
    case 'premium':
      return { key, label, value: money(t.premium), tone: 'none' }
    case 'bought':
      return { key, label, value: money(t.priceWhenBought), tone: 'none' }
    case 'current':
      return { key, label, value: money(t.currentPrice), tone: 'none' }
    case 'optionPrice':
      return { key, label, value: money(t.currentOptionPrice), tone: 'none' }
    case 'qty':
      return { key, label, value: String(t.qty ?? '—'), tone: 'none' }
    case 'total':
      return { key, label, value: money(t.total), tone: 'none' }
    case 'itmOtm':
      return { key, label, value: itmOtm(t), tone: 'none' }
    case 'closeAction':
      return { key, label, value: t.closeAction || '—', tone: 'none' }
    case 'closeDate':
      return { key, label, value: mdy(t.closeDate), tone: 'none' }
    case 'netPnl':
      return { key, label, value: money(t.netPosition), tone: t.netPosition > 0 ? 'pos' : t.netPosition < 0 ? 'neg' : 'muted' }
    case 'return':
      return {
        key,
        label,
        value: t.returnPct != null ? `${t.returnPct.toFixed(1)}%` : '—',
        tone: t.returnPct > 0 ? 'pos' : t.returnPct < 0 ? 'neg' : 'muted',
      }
    default:
      return { key, label, value: '—', tone: 'none' }
  }
}

// ---------------------------------------------------------------------------
// Hardcoded default field lists (Option A). Edit here to change what shows.
// ---------------------------------------------------------------------------

/** Collapsed phone card — prominent top line. */
export const ROW1_FIELDS: TradeFieldKey[] = ['ticker', 'strategy', 'status', 'netPnl']
/** Collapsed phone card — secondary line. */
export const ROW2_FIELDS: TradeFieldKey[] = ['strike', 'exp', 'daysLeft']

/** Expanded phone card — full detail (order matters). */
export const DETAIL_FIELDS: TradeFieldKey[] = [
  'premium',
  'optionPrice',
  'buyDate',
  'daysLeft',
  'itmOtm',
  'total',
  'netPnl',
  'return',
]

/** Extra detail fields shown only for closed trades. */
export const CLOSED_DETAIL_FIELDS: TradeFieldKey[] = ['closeAction', 'closeDate']

/** Tablet / landscape richer single-column row — inline fields. */
export const TABLET_ROW_FIELDS: TradeFieldKey[] = [
  'ticker',
  'strategy',
  'status',
  'strike',
  'exp',
  'daysLeft',
  'premium',
  'netPnl',
  'return',
]

// ---------------------------------------------------------------------------
// Editable field descriptors for the mobile card inline edit (Bug: mobile could
// not edit active trade values). Maps a card display key -> the real Trade model
// field name + the InlineEdit input type. Derived/computed keys (daysLeft, days,
// itmOtm, total, netPnl, return, status) are intentionally absent = read-only.
// Keep editable keys in sync with the desktop table's inline-editable fields.
// ---------------------------------------------------------------------------

export interface EditableFieldDescriptor {
  /** The actual Trade model field name passed to the save handler. */
  modelField: string
  /** InlineEdit input type. */
  type: 'text' | 'number' | 'date' | 'select'
}

export const EDITABLE_CARD_FIELDS: Partial<Record<TradeFieldKey, EditableFieldDescriptor>> = {
  strike: { modelField: 'strikePrice', type: 'number' },
  exp: { modelField: 'expDate', type: 'date' },
  buyDate: { modelField: 'buyDate', type: 'date' },
  premium: { modelField: 'premium', type: 'number' },
  bought: { modelField: 'priceWhenBought', type: 'number' },
  current: { modelField: 'currentPrice', type: 'number' },
  optionPrice: { modelField: 'currentOptionPrice', type: 'number' },
  qty: { modelField: 'qty', type: 'number' },
  closeAction: { modelField: 'closeAction', type: 'select' },
  closeDate: { modelField: 'closeDate', type: 'date' },
}

/** Raw (unformatted) current value for an editable field, for seeding InlineEdit. */
export function rawFieldValue(t: TradeLike, key: TradeFieldKey): string | number {
  switch (key) {
    case 'strike':
      return t.strikePrice ?? 0
    case 'exp':
      return t.expDate ?? ''
    case 'buyDate':
      return t.buyDate ?? ''
    case 'premium':
      return t.premium ?? 0
    case 'bought':
      return t.priceWhenBought ?? 0
    case 'current':
      return t.currentPrice ?? 0
    case 'optionPrice':
      return t.currentOptionPrice ?? 0
    case 'qty':
      return t.qty ?? 0
    case 'closeAction':
      return t.closeAction ?? ''
    case 'closeDate':
      return t.closeDate ?? ''
    default:
      return ''
  }
}
